// Auth Context Provider
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { authService } from '@/services/firebase/auth';
import type { UserProfile } from '@/types';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  error: Error | null;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Set up auth state listener and check for OAuth redirect results
  useEffect(() => {
    let isMounted = true;

    // First, check for any pending OAuth redirect result
    // This is needed because after a redirect-based OAuth flow,
    // we need to explicitly retrieve the result
    const checkRedirect = async () => {
      try {
        const redirectResult = await authService.checkRedirectResult();
        if (redirectResult && isMounted) {
          console.log('Setting user from redirect result');
          setUser(redirectResult.user);
          setProfile(redirectResult.profile);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking redirect result:', err);
      }
    };

    // Check for redirect result immediately
    checkRedirect();

    // Also set up the auth state listener for normal auth state changes
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      if (!isMounted) return;

      setUser(firebaseUser);

      if (firebaseUser) {
        try {
          const userProfile = await authService.getUserProfile(firebaseUser.uid, firebaseUser);
          if (isMounted) {
            setProfile(userProfile);
          }
        } catch (err) {
          if (isMounted) {
            setError(err instanceof Error ? err : new Error('Failed to fetch profile'));
          }
        }
      } else {
        setProfile(null);
      }

      if (isMounted) {
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  const signInWithGoogle = async () => {
    setError(null);
    try {
      const result = await authService.signInWithGoogle();
      setUser(result.user);
      setProfile(result.profile);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Google sign-in failed'));
      throw err;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    setError(null);
    try {
      const result = await authService.signInWithEmail(email, password);
      setUser(result.user);
      setProfile(result.profile);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Email sign-in failed'));
      throw err;
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    setError(null);
    try {
      const result = await authService.signUpWithEmail(email, password);
      setUser(result.user);
      setProfile(result.profile);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Sign-up failed'));
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await authService.signOut();
      setUser(null);
      setProfile(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Sign-out failed'));
      throw err;
    }
  };

  const refreshProfile = async () => {
    if (!user) return;
    try {
      const userProfile = await authService.getUserProfile(user.uid, user);
      setProfile(userProfile);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to refresh profile'));
    }
  };

  const isSuperAdmin = authService.isSuperAdmin(profile);

  const value: AuthContextType = {
    user,
    profile,
    loading,
    error,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    refreshProfile,
    isSuperAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
